import React, { createContext, useEffect, useState } from 'react';

import { EOnboardingStep } from 'enums';
import { useOnboarding, useUpdateOnboardingMutation } from 'store/apis';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices';

type Onboarding = {
  status: EOnboardingStep;
  buildingId: number | null;
  zoneId: number | null;
  employeeId: number | null;
};

type OnboardingContextType = Onboarding & {
  updateOnboarding: (value: Partial<Onboarding>) => void;
  visible: boolean;
  setVisible: (value: boolean) => void;
};

const defaultOnboardingContextValue = {
  status: EOnboardingStep.SETUP_BUILDING,
  buildingId: null,
  zoneId: null,
  employeeId: null,
  updateOnboarding: () => {},
  visible: true,
  setVisible: () => {},
};

export const OnboardingContext = createContext<OnboardingContextType>(defaultOnboardingContextValue);

interface OnboardingContextProviderProps {
  children?: any;
}

export const OnboardingContextProvider = ({ children }: OnboardingContextProviderProps) => {
  const {
    data: { status: _status, buildingId: _buildingId, zoneId: _zoneId, employeeId: _employeeId },
    isLoading: loading_onboarding,
  } = useOnboarding();
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [updateOnboardingMutation] = useUpdateOnboardingMutation();
  const [visible, setVisible] = useState<boolean>(true);

  const [{ status, buildingId, zoneId, employeeId }, setOnboarding] = useState<Onboarding>({
    status: _status,
    buildingId: _buildingId,
    zoneId: _zoneId,
    employeeId: _employeeId,
  });

  const updateOnboarding = (value: Partial<Onboarding>) => setOnboarding((prev) => ({ ...prev, ...value }));

  useEffect(() => {
    if (!loading_onboarding) {
      setOnboarding({
        status: _status,
        buildingId: _buildingId,
        zoneId: _zoneId,
        employeeId: _employeeId,
      });

      if (_status === EOnboardingStep.COMPLETED) {
        setVisible(false);
      }
    }
  }, [_status, _buildingId, _zoneId, _employeeId, loading_onboarding]);

  useEffect(() => {
    if ((status && (buildingId || zoneId || employeeId)) || status === EOnboardingStep.COMPLETED) {
      updateOnboardingMutation({ orgSlug, onboarding: { status, buildingId, zoneId, employeeId } });
    }
  }, [status, buildingId, zoneId, employeeId]);

  return loading_onboarding ? null : (
    <OnboardingContext.Provider
      value={{ status, buildingId, zoneId, employeeId, updateOnboarding, visible, setVisible }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};
